import type { MouseEvent } from 'react';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { FundAccountButton } from '@/shared/components/dashboards/FundAccount';
import Button from '@/shared/components/flip-ui-kit/Button';
import AssetPoolLogo from '@/shared/components/logo/AssetPool';
import { TopStrategyPill } from '@/shared/components/pills';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import CopyPlusIcon from '@/shared/icons/flip-ui-kit/large/CopyPlus';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import ChartIcon from '@/shared/icons/flip-ui-kit/small/Chart';
import DropletIcon from '@/shared/icons/flip-ui-kit/small/Droplet';
import Target from '@/shared/icons/flip-ui-kit/small/Target';
import Transfer from '@/shared/icons/flip-ui-kit/small/Transfer';
import UsersIcon from '@/shared/icons/flip-ui-kit/small/Users';
import { apyToText } from '@/shared/utils/boost';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import {
  convertedTick,
  isAllowedStrategyType,
  templateKeySchema,
  type StrategyTemplate,
} from '@/shared/utils/strategies';
import Pill from '../flip-ui-kit/Pill';

export const StrategyTemplateCard = ({
  pill,
  template,
  onClick,
  onCreateStrategy,
  onAddLiquidity,
  onViewDetails,
  app = 'LP',
  accountIsEmpty,
}: {
  pill?: 'popular' | 'volume' | 'apy';
  template: StrategyTemplate;
  onClick?: (e: MouseEvent) => void;
  onCreateStrategy: () => void;
  onAddLiquidity: () => void;
  onViewDetails: () => void;
  app?: 'LP' | 'Explorer';
  accountIsEmpty?: boolean;
}) => {
  const {
    baseAsset: baseAssetName,
    quoteAsset: quoteAssetName,
    templateKey,
    totalBaseLiquidity,
    totalQuoteLiquidity,
    instanceCount: lpCount,
    reportingData,
  } = template;

  const baseAsset = chainflipAssetMap[baseAssetName];
  const quoteAsset = chainflipAssetMap[quoteAssetName];

  const sharedButtonProps = {
    type: 'primary-standard' as const,
    size: 'medium' as const,
    className: 'grow whitespace-nowrap',
    iconPos: 'left' as const,
    icon: <CopyPlusIcon className="mr-1 " />,
    onClick: (e: MouseEvent) => {
      e.stopPropagation();
      if (template.ownInstance) {
        onAddLiquidity();
      } else {
        onCreateStrategy();
      }
    },
    children: template.ownInstance ? 'Add Liquidity' : 'Copy Strategy',
  };

  const { minBuyTick, maxBuyTick, minSellTick, maxSellTick, type } =
    templateKeySchema.parse(templateKey);

  const label = type === 'ORACLE_TRACKING' ? 'Offset' : 'Price';

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      className="bg-holy-radial-gray-3-5 hover:bg-holy-radial-gray-7 flex cursor-pointer flex-col gap-6 rounded-md border border-cf-gray-5 p-6"
    >
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2 font-aeonikMedium">
          <AssetPoolLogo asset={baseAsset.chainflipId} size="medium" />
          <div className="text-16 leading-4 text-cf-light-3">
            {baseAsset.assetAndChainName}-{quoteAsset.assetAndChainName}
          </div>
        </div>
        <div>{pill && <TopStrategyPill type={pill} />}</div>
      </div>
      <div className="flex h-[60px] flex-row items-center gap-x-1.5 font-aeonikMedium">
        {reportingData ? (
          <>
            <div className="text-[40px] text-cf-white">{apyToText(reportingData.apyPercent)}</div>
            <div className="text-16 text-cf-light-2">Estimated APY</div>
            <QuestionMarkTooltip content="Estimated annual yield based on recent earned fees data." />
          </>
        ) : (
          <SkeletonLine width={100} height={40} />
        )}
      </div>
      <div className="flex flex-col gap-3 font-aeonikMedium text-14 text-cf-light-2">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-x-0.5">
            <ArrowIcon direction="down" />
            <div>Buy {label}</div>
          </div>
          <div className="text-right">
            {convertedTick(minBuyTick, maxBuyTick, baseAssetName, type)}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-x-0.5">
            <ArrowIcon direction="up" />
            <div>Sell {label}</div>
          </div>
          <div className="text-right">
            {convertedTick(minSellTick, maxSellTick, baseAssetName, type)}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-x-0.5">
            <DropletIcon />
            <div>Liquidity</div>
          </div>
          <div className="text-right text-cf-white">
            <span className="whitespace-nowrap">
              {totalBaseLiquidity.toFormatted('numeral')} {baseAsset.assetAndChainName}
            </span>{' '}
            /{' '}
            <span className="whitespace-nowrap">
              {totalQuoteLiquidity.toFormatted('numeral')} {quoteAsset.assetAndChainName}
            </span>
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-x-0.5">
            <ChartIcon />
            <div>Volume</div>
          </div>
          <div className="text-right text-cf-white">
            {reportingData ? (
              formatUsdValue(reportingData.totalFilledVolumeUsd)
            ) : (
              <SkeletonLine width={50} />
            )}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-x-0.5">
            <UsersIcon />
            <div>Liquidity Providers</div>
          </div>
          <div className="text-right text-cf-white">{lpCount}</div>
        </div>
        {isAllowedStrategyType(template.type) && (
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center gap-x-0.5">
              <Target />
              <div>Strategy type</div>
            </div>
            <Pill
              text={template.type === 'INVENTORY_BASED' ? 'Hard peg' : 'Oracle-based'}
              color="neutral"
              Icon={Transfer}
            />
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-row gap-2">
        {app === 'LP' ? (
          <FundAccountButton {...sharedButtonProps} accountIsEmpty={accountIsEmpty} />
        ) : (
          <Button {...sharedButtonProps} />
        )}
        <Button
          type="secondary-standard"
          className="whitespace-nowrap"
          size="medium"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails();
          }}
        >
          View Details
        </Button>
      </div>
    </div>
  );
};
